import { Link } from 'react-router-dom';
import { ArrowRight, Search, TrendingUp, FileText, Link2, BarChart, MapPin } from 'lucide-react';
import { PageSEO } from '@/components/SEO';

const tjenester = [
  {
    icon: Search,
    title: 'Søkeordanalyse',
    description: 'Vi identifiserer de mest verdifulle søkeordene for din bransje.',
  },
  {
    icon: FileText,
    title: 'Innholdsoptimalisering',
    description: 'Vi optimaliserer eksisterende innhold og skaper nytt som rangerer.',
  },
  {
    icon: Link2,
    title: 'Linkbygging',
    description: 'Kvalitative lenker fra relevante nettsteder for bedre autoritet.',
  },
  {
    icon: TrendingUp,
    title: 'Teknisk SEO',
    description: 'Sikrer at nettsiden din er teknisk optimalisert for søkemotorer.',
  },
  {
    icon: MapPin,
    title: 'Lokal SEO',
    description: 'Bli synlig for kunder som søker lokalt etter dine tjenester.',
  },
  {
    icon: BarChart,
    title: 'Rapportering',
    description: 'Månedlige rapporter med rangering, trafikk og resultater.',
  },
];

const resultater = [
  { metric: '150%', description: 'Gjennomsnittlig økning i organisk trafikk' },
  { metric: '85%', description: 'Av kundene på første side i Google' },
  { metric: '12 mnd', description: 'Gjennomsnittlig tid til lønnsomhet' },
];

export default function SEO() {
  return (
    <div>
      <PageSEO title="SEO Norge – Bli synlig på Google" path="/tjenester/seo" description="Søkemotoroptimalisering (SEO) for bedrifter i hele Norge. Vi hjelper deg å klatre på Google og tiltrekke flere kunder organisk. Få en gratis analyse i dag." />
      {/* Hero */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="seo-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                Tjeneste
              </p>
              <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
                Bli synlig på Google med SEO
              </h1>
              <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
                Søkemotoroptimalisering handler om å bli funnet av de riktige kundene. Vi hjelper deg med å klatre i søkeresultatene og få mer organisk trafikk.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group mt-8"
                data-testid="seo-cta"
              >
                Få en SEO-analyse
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            <div className="relative group rounded-3xl overflow-hidden shadow-2xl ring-1 ring-primary/5">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-accent/20 rounded-3xl" />
              <img
                src="https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&q=80"
                alt="SEO optimalisering og digital vekst – Side Kraft"
                className="relative z-10 w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Resultater */}
      <section className="py-16 border-b border-border" data-testid="seo-resultater">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {resultater.map((resultat, index) => (
              <div key={index} className="text-center p-8 rounded-2xl bg-card border border-border/50 shadow-sm transition-all hover:shadow-md" data-testid={`resultat-${index}`}>
                <p className="font-syne font-bold text-5xl text-accent mb-2">
                  {resultat.metric}
                </p>
                <p className="font-manrope text-sm text-muted-foreground">
                  {resultat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tjenester */}
      <section className="py-24 md:py-32" data-testid="seo-tjenester">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Hva vi gjør
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight">
              Komplett SEO-tjeneste
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tjenester.map((tjeneste, index) => (
              <div
                key={index}
                className="p-8 border border-border rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`seo-tjeneste-${index}`}
              >
                <tjeneste.icon className="w-10 h-10 text-accent mb-6" />
                <h3 className="font-syne font-bold text-xl text-primary mb-3">
                  {tjeneste.title}
                </h3>
                <p className="font-manrope text-sm text-muted-foreground leading-relaxed">
                  {tjeneste.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground" data-testid="seo-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight mb-6">
            Klar for bedre synlighet?
          </h2>
          <p className="font-manrope text-primary-foreground/70 max-w-xl mx-auto mb-10">
            Få en gratis SEO-analyse av nettsiden din og se hvor stort potensial du har.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-accent text-accent-foreground hover:bg-primary-foreground hover:text-primary transition-colors duration-300 group"
            data-testid="seo-bottom-cta"
          >
            Bestill gratis analyse
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
}
